// Slice 11 — Custom Report Builder.
// Pick a source, choose dimensions + measures, optional filters, run + chart + table + CSV + save.
import { useEffect, useMemo, useState } from "react";
import {
  Sliders, Loader2, Play, Download, Save, Trash2, RefreshCw, Plus, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ReportChart } from "./_charts";
import { RangeControl, rangeParams, isoToday, isoDaysAgo } from "./_daterange";

const CHARTS = [
  { id: "table", label: "Table only" },
  { id: "bar",   label: "Bar" },
  { id: "line",  label: "Line" },
  { id: "pie",   label: "Pie" },
];

export default function CustomReportBuilderPage() {
  const [sources, setSources] = useState([]);
  const [sourceId, setSourceId] = useState("cdrs");
  const [dims, setDims] = useState(["date"]);
  const [meas, setMeas] = useState(["count"]);
  const [filters, setFilters] = useState({});
  const [rng, setRng] = useState("today");
  const [from, setFrom] = useState(isoToday());
  const [to, setTo] = useState(isoToday());
  const [chart, setChart] = useState("bar");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  // Save UI
  const [saved, setSaved] = useState([]);
  const [name, setName] = useState("");

  useEffect(() => {
    api.get("/reports/sources").then((r) => setSources(r.data.sources || []))
       .catch((e) => toast.error(formatErr(e)));
    loadSaved();
  }, []);

  async function loadSaved() {
    try { const r = await api.get("/reports/saved"); setSaved(r.data.reports || []); }
    catch { /* silent */ }
  }

  const src = useMemo(() => sources.find((s) => s.id === sourceId), [sources, sourceId]);

  // When user switches source, reset selections to safe defaults.
  function switchSource(id) {
    const s = sources.find((x) => x.id === id);
    setSourceId(id);
    setDims(s?.dimensions?.[0] ? [s.dimensions[0].id] : []);
    setMeas(s?.measures?.[0] ? [s.measures[0].id] : ["count"]);
    setFilters({});
    setResult(null);
  }

  function toggle(arr, setArr, v) {
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  }

  async function run() {
    if (!src) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/reports/custom/run", {
        source: sourceId, dimensions: dims, measures: meas,
        filters, ...rangeParams(rng, from, to),
      });
      setResult({ ...r.data, chart });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  async function exportCsv() {
    if (!result) return;
    try {
      const r = await api.post("/reports/export.csv", {
        source: sourceId, dimensions: dims, measures: meas,
        filters, ...rangeParams(rng, from, to),
      }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${sourceId}-custom-${rng}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) { toast.error(formatErr(e)); }
  }

  async function save() {
    if (!name.trim()) { toast.error("Give the report a name first"); return; }
    try {
      await api.post("/reports/saved", {
        name: name.trim(), source: sourceId, dimensions: dims,
        measures: meas, filters, chart, range_str: rng,
      });
      toast.success("Report saved");
      setName("");
      loadSaved();
    } catch (e) { toast.error(formatErr(e)); }
  }

  async function loadSavedReport(r) {
    setSourceId(r.source);
    setDims(r.dimensions || []);
    setMeas(r.measures || ["count"]);
    setFilters(r.filters || {});
    setChart(r.chart || "bar");
    setRng(r.range_str || "30d");
    setResult(null);
    toast.info(`Loaded: ${r.name}`);
  }

  async function deleteSaved(id) {
    if (!window.confirm("Delete this saved report?")) return;
    try { await api.delete(`/reports/saved/${id}`); loadSaved(); toast.success("Deleted"); }
    catch (e) { toast.error(formatErr(e)); }
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-7xl" data-testid="custom-builder-page">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <Sliders className="w-5 h-5 text-primary"/> Custom Report Builder
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pick a source, choose dimensions + measures, optional filters — run, chart, export, save.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[340px_1fr] gap-5">
        {/* LEFT — builder */}
        <Card className="p-5 space-y-4 self-start" data-testid="cb-panel">
          <div>
            <Label className="text-xs uppercase font-bold tracking-wider mb-2 block">Source</Label>
            <Select value={sourceId} onValueChange={switchSource}>
              <SelectTrigger data-testid="cb-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase font-bold tracking-wider mb-2 block">Dimensions</Label>
            <div className="flex flex-wrap gap-1.5">
              {src?.dimensions.map((d) => (
                <button
                  key={d.id}
                  data-testid={`cb-dim-${d.id}`}
                  onClick={() => toggle(dims, setDims, d.id)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-all
                    ${dims.includes(d.id)
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card border-border hover:border-primary/40"}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase font-bold tracking-wider mb-2 block">Measures</Label>
            <div className="flex flex-wrap gap-1.5">
              {src?.measures.map((m) => (
                <button
                  key={m.id}
                  data-testid={`cb-meas-${m.id}`}
                  onClick={() => toggle(meas, setMeas, m.id)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-all
                    ${meas.includes(m.id)
                      ? "bg-secondary text-secondary-foreground border-secondary"
                      : "bg-card border-border hover:border-secondary/40"}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {src?.filters?.length > 0 && (
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-2 block">Filters</Label>
              <div className="space-y-2">
                {src.filters.map((f) => (
                  <div key={f} className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-28 shrink-0">{f}</span>
                    <Input
                      data-testid={`cb-filter-${f}`}
                      value={filters[f] || ""}
                      onChange={(e) => setFilters({ ...filters, [f]: e.target.value })}
                      placeholder="any"
                      className="h-8 text-xs flex-1"
                    />
                    {filters[f] && (
                      <button onClick={() => { const { [f]: _, ...rest } = filters; setFilters(rest); }} className="text-muted-foreground hover:text-foreground">
                        <X className="w-3.5 h-3.5"/>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Range</Label>
              <RangeControl rng={rng} setRng={setRng} from={from} to={to} setFrom={setFrom} setTo={setTo} testid="cb-range" />
            </div>
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Chart</Label>
              <Select value={chart} onValueChange={setChart}>
                <SelectTrigger className="h-8 text-xs" data-testid="cb-chart"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHARTS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button onClick={run} disabled={busy || !src || !meas.length} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5" data-testid="cb-run">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Play className="w-4 h-4"/>}
            Run report
          </Button>

          <div className="pt-3 border-t border-border space-y-2">
            <Label className="text-xs uppercase font-bold tracking-wider block">Save as</Label>
            <div className="flex gap-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My report name"
                className="h-8 text-xs flex-1"
                data-testid="cb-save-name"
              />
              <Button onClick={save} disabled={!name.trim()} variant="outline" className="h-8 gap-1.5 text-xs" data-testid="cb-save">
                <Save className="w-3.5 h-3.5"/> Save
              </Button>
            </div>
          </div>
        </Card>

        {/* RIGHT — result + saved reports */}
        <div className="space-y-4 min-w-0">
          <Card className="p-5 min-w-0" data-testid="cb-result">
            <div className="flex items-baseline justify-between gap-2 mb-3 flex-wrap">
              <h2 className="font-display text-lg font-bold tracking-tight">Result</h2>
              <div className="flex gap-2">
                <Button onClick={exportCsv} disabled={!result} variant="outline" className="gap-1.5 h-8 text-xs" data-testid="cb-csv">
                  <Download className="w-3.5 h-3.5"/> CSV
                </Button>
              </div>
            </div>
            {busy && (
              <div className="h-[260px] flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground"/>
              </div>
            )}
            {!busy && !result && (
              <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground italic">
                Configure on the left and hit <span className="font-mono mx-1">Run report</span> to see results.
              </div>
            )}
            {!busy && result && (
              <>
                {chart !== "table" && (
                  <ReportChart kind={chart} columns={result.columns} rows={result.rows} height={300}/>
                )}
                <div className="mt-4 overflow-x-auto rounded-md border border-border max-h-[420px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 sticky top-0">
                      <tr>
                        {result.columns.map((c) => (
                          <th key={c.id} className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider">{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.length === 0 && (
                        <tr><td colSpan={result.columns.length} className="px-3 py-8 text-center text-sm text-muted-foreground italic">No rows.</td></tr>
                      )}
                      {result.rows.map((r, i) => (
                        <tr key={i} className="odd:bg-card even:bg-muted/20 border-t border-border/60">
                          {result.columns.map((c) => (
                            <td key={c.id} className="px-3 py-2 font-mono text-xs">{r[c.id] ?? ""}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>

          {/* Saved reports */}
          <Card className="p-5" data-testid="cb-saved">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold">Saved reports</h3>
              <Badge variant="outline" className="text-[10px]">{saved.length} saved</Badge>
            </div>
            {saved.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">Nothing saved yet — build a report above and click Save.</p>
            ) : (
              <ul className="divide-y divide-border/60">
                {saved.map((r) => (
                  <li key={r.id} className="flex items-center gap-3 py-2.5" data-testid={`cb-saved-${r.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{r.name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {r.source} · {r.dimensions?.join(", ") || "no dims"} · {r.measures?.join(", ")} · {r.range_str}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => loadSavedReport(r)} className="h-8 text-xs">Load</Button>
                    <button onClick={() => deleteSaved(r.id)} className="text-muted-foreground hover:text-red-600 p-1" data-testid={`cb-saved-del-${r.id}`}>
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
